import { useCallback, useMemo } from 'react';

import { useShowConsumerStoreItem } from '@givebacks/api/store';
import { toTitleCase } from '@givebacks/utils/strings';

import { useShowCause } from '../../api';
import contributor from '../../utils/translations/crowdfunding/contributor.json';
import donor from '../../utils/translations/crowdfunding/donor.json';

const TRANSLATIONS = { contributor, donor };

type DonationTranslations = 'contributor' | 'donor';

export const useTranslations = (
  locale: keyof typeof TRANSLATIONS | DonationTranslations,
  isLoading?: boolean
) => {
  const t = useCallback(
    (keyString: keyof (typeof TRANSLATIONS)[typeof locale]) => {
      if (isLoading) return '';
      if (!locale) {
        return keyString;
      }
      return TRANSLATIONS?.[locale]?.[keyString] || keyString;
    },
    [TRANSLATIONS, locale, isLoading]
  );

  return { t };
};

// This is specific for crowdfunding. It would probably not be great if we didn't account for supporting different languages in the future
export const useCrowdFundingTranslations = (uuid?: string) => {
  const { data: cause, isLoading } = useShowCause(uuid);

  return useTranslations(cause?.donor_convention ?? 'donor', !!uuid && isLoading);
};

export const useCheckoutTranslations = (storeItemId?: string, causeId?: string) => {
  const { data: storeItemType, isLoading } = useShowConsumerStoreItem({
    uuid: storeItemId,
    causeId,
    select: (data) => data.__type,
  });
  const key = useMemo(() => {
    switch (storeItemType) {
      case 'donation':
      case 'athon':
      case 'campaign':
        return 'donate';
      default:
        return 'buy';
    }
  }, [storeItemType]);

  return {
    titleCase: toTitleCase(key),
    checkoutAction: key,
    isLoading,
  };
};
